import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tracks as tracksApi } from "../../lib/api";
import { useUI } from "../../lib/store";
import { notify } from "../../lib/notifications";
import type { BulkAttachTaskRequest, Task, Track } from "../../lib/types";
import { Button } from "../shared/Button";
import { Modal } from "../shared/Modal";
import { TrackEditor } from "../tracks/TrackEditor";
import { cn } from "../../lib/cn";

/**
 * Floating bar for adding several un-started cards to a track at once. Appears
 * when the operator ticks one or more eligible (backlog/todo, untracked) cards;
 * "Add to track" opens a modal that picks the destination track + milestone and
 * POSTs the whole set to the bulk-attach endpoint in one transaction.
 */
export function BulkAddToTrackBar({ tasks }: { tasks: Record<string, Task> }) {
  const selection = useUI((s) => s.trackSelection);
  const clear = useUI((s) => s.clearTrackSelection);
  const [open, setOpen] = useState(false);

  // Only ids still present and still eligible are actionable.
  const selected = selection
    .map((id) => tasks[id])
    .filter(
      (t): t is Task =>
        Boolean(t) &&
        !t.track_id &&
        (t.status === "backlog" || t.status === "todo"),
    );

  if (selection.length === 0) return null;

  return (
    <>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
        <div
          className={cn(
            "flex items-center gap-3 bg-bg-surface rounded-full shadow-lg",
            "border border-border-default pl-4 pr-2 py-2",
          )}
        >
          <span className="text-small text-text-secondary">
            {selected.length} selected
          </span>
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            disabled={selected.length === 0}
          >
            Add to track
          </Button>
          <Button size="sm" variant="ghost" onClick={clear}>
            Clear
          </Button>
        </div>
      </div>
      {open && selected.length > 0 && (
        <BulkAddToTrackModal
          selected={selected}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            clear();
          }}
        />
      )}
    </>
  );
}

type Destination =
  | { kind: "existing"; idx: number }
  | { kind: "new"; title: string };

function BulkAddToTrackModal({
  selected,
  onClose,
  onDone,
}: {
  selected: Task[];
  onClose: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const projectId = selected[0].project_id;

  const { data: projectTracks = [] } = useQuery({
    queryKey: ["tracks", projectId],
    queryFn: () => tracksApi.list(projectId),
  });

  const [trackId, setTrackId] = useState<string>("");
  const [creatingTrack, setCreatingTrack] = useState(false);
  const [dest, setDest] = useState<Destination>({ kind: "new", title: "" });
  const [acceptance, setAcceptance] = useState("");

  // The chosen track's full plan (for the milestone picker).
  const { data: fullTrack } = useQuery({
    queryKey: ["tracks", trackId, "detail"],
    queryFn: () => tracksApi.get(trackId),
    enabled: !!trackId,
  });
  const milestones = fullTrack?.plan?.milestones ?? [];
  const hasMilestones = milestones.length > 0;

  // Default the destination once the track's milestones load.
  useEffect(() => {
    if (!trackId) return;
    setDest(
      hasMilestones
        ? { kind: "existing", idx: Math.max(fullTrack?.active_milestone ?? 0, 0) }
        : { kind: "new", title: "" },
    );
  }, [trackId, hasMilestones, fullTrack?.active_milestone]);

  const attach = useMutation({
    mutationFn: () => {
      const body: BulkAttachTaskRequest = {
        task_ids: selected.map((t) => t.id),
        acceptance: lines(acceptance),
        ...(dest.kind === "existing"
          ? { milestone_idx: dest.idx }
          : { new_milestone: { title: dest.title.trim(), acceptance: lines(acceptance) } }),
      };
      return tracksApi.attachTasksBulk(trackId, body);
    },
    onSuccess: (attached) => {
      qc.invalidateQueries({ queryKey: ["kanban"] });
      qc.invalidateQueries({ queryKey: ["tracks"] });
      notify({
        severity: "success",
        title: `Added ${attached.length} task${attached.length === 1 ? "" : "s"} to the track`,
      });
      onDone();
    },
    onError: (e) => {
      notify({
        severity: "error",
        title: "Add to track failed",
        detail: e instanceof Error ? e.message : String(e),
      });
    },
  });

  if (creatingTrack) {
    return (
      <Modal onClose={() => setCreatingTrack(false)} title="New track" size="lg">
        <TrackEditor
          projectId={projectId}
          onCancel={() => setCreatingTrack(false)}
          onCreated={(created: Track) => {
            setCreatingTrack(false);
            qc.invalidateQueries({ queryKey: ["tracks", projectId] });
            setTrackId(created.id);
          }}
        />
      </Modal>
    );
  }

  const newTitleValid = dest.kind !== "new" || dest.title.trim().length > 0;
  const canSubmit = !!trackId && newTitleValid && !attach.isPending;

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <Button variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" disabled={!canSubmit} onClick={() => attach.mutate()}>
        {attach.isPending
          ? "Adding…"
          : `Add ${selected.length} to track`}
      </Button>
    </div>
  );

  return (
    <Modal
      onClose={onClose}
      size="md"
      title={`Add ${selected.length} tasks to a track`}
      description="The selected tasks join the track and run when it's dispatched."
      footer={footer}
    >
      <div className="space-y-5">
        {/* Track picker */}
        <div className="space-y-2">
          <div className="text-caption text-text-tertiary">Track</div>
          <div className="flex items-center gap-2">
            <select
              value={trackId}
              onChange={(e) => setTrackId(e.target.value)}
              className={cn(
                "flex-1 bg-bg-inset text-text-primary text-body rounded-md",
                "border border-border-default px-3 py-2",
                "focus:outline-none focus:ring-1 focus:ring-accent",
              )}
            >
              <option value="">Select a track…</option>
              {projectTracks.map((tr) => (
                <option key={tr.id} value={tr.id}>
                  {tr.title}
                </option>
              ))}
            </select>
            <Button size="sm" variant="secondary" onClick={() => setCreatingTrack(true)}>
              New track…
            </Button>
          </div>
        </div>

        {/* Destination (once a track is chosen) */}
        {trackId && (
          <div className="space-y-2">
            <div className="text-caption text-text-tertiary">Milestone</div>
            <div className="flex gap-2">
              <DestTab
                active={dest.kind === "existing"}
                disabled={!hasMilestones}
                onClick={() =>
                  setDest({
                    kind: "existing",
                    idx: Math.max(fullTrack?.active_milestone ?? 0, 0),
                  })
                }
              >
                Existing milestone
              </DestTab>
              <DestTab
                active={dest.kind === "new"}
                onClick={() => setDest({ kind: "new", title: "" })}
              >
                New milestone
              </DestTab>
            </div>

            {dest.kind === "existing" ? (
              <select
                value={dest.idx}
                onChange={(e) => setDest({ kind: "existing", idx: Number(e.target.value) })}
                className={cn(
                  "w-full bg-bg-inset text-text-primary text-body rounded-md",
                  "border border-border-default px-3 py-2",
                  "focus:outline-none focus:ring-1 focus:ring-accent",
                )}
              >
                {milestones.map((m, i) => (
                  <option key={i} value={i}>
                    {`Milestone ${i + 1}`}
                    {m.title ? ` — ${m.title}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input
                autoFocus
                value={dest.title}
                onChange={(e) => setDest({ kind: "new", title: e.target.value })}
                placeholder="New milestone title"
                className={cn(
                  "w-full bg-bg-inset text-text-primary text-body rounded-md",
                  "border border-border-default px-3 py-2",
                  "focus:outline-none focus:ring-1 focus:ring-accent",
                )}
              />
            )}
            <textarea
              value={acceptance}
              onChange={(e) => setAcceptance(e.target.value)}
              rows={3}
              placeholder="Acceptance criteria for this milestone — one per line (optional)"
              className={cn(
                "w-full bg-bg-inset text-text-primary text-small rounded-md",
                "border border-border-default px-3 py-2 resize-y",
                "focus:outline-none focus:ring-1 focus:ring-accent",
              )}
            />
          </div>
        )}

        {/* Selected tasks */}
        <div className="space-y-2">
          <div className="text-caption text-text-tertiary">
            {selected.length} task{selected.length === 1 ? "" : "s"}
          </div>
          <ul className="max-h-40 overflow-y-auto divide-y divide-border-subtle border border-border-subtle rounded-md">
            {selected.map((t) => (
              <li
                key={t.id}
                className="px-3 py-1.5 text-body text-text-primary truncate"
              >
                {t.title}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Modal>
  );
}

function lines(text: string): string[] {
  return text
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

function DestTab({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 text-small rounded-md border transition-colors duration-fast ease-out",
        active
          ? "border-accent-muted bg-accent-soft text-text-primary"
          : "border-border-subtle text-text-secondary hover:bg-bg-hover",
        disabled && "opacity-40 cursor-not-allowed",
      )}
    >
      {children}
    </button>
  );
}
